import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';
import type { SaveIntakeOptions } from './workout.service';

// Enums for medication properties
export type MedicationForm = 'tablets' | 'capsules' | 'syrup' | 'drops' | 'solution' | 'cream' | 'ointment' | 'powder' | 'spray' | 'other';
// Assuming Dosage is now an array of strings, not a single enum
export type MedicationSourceType = 'system' | 'user' | 'all' | 'legacy_preparat';
export type MedicationSortBy = 'id' | 'form' | 'name'; // Removed dosage from sort by
export type SortOrder = 'ASC' | 'DESC';
export type Locale = 'uk' | 'en' | 'ru' | 'cs' | 'es' | 'pl' | 'be';

// Medication DTOs
export interface MedicationResponseDto {
  id: number;
  name: string;
  form: string;
  dosage: string[] | null; // Changed to string[] | null
  createdByUserId: number | null;
  isStructured: boolean;
  isSystem: boolean; // Added isSystem
  usageCount: number; // Added usageCount
  availableTranslations: { [key in Locale]?: boolean }; // Added availableTranslations
  imageUrl?: string | null; // Added imageUrl
  externalLink?: string | null; // Added externalLink
  legacyId?: string | null; // Added legacyId
  legacyEntity?: 'preparat' | null; // Added legacyEntity
  legacyImg?: string | null; // Added legacyImg
}

export interface MedicationEditDto {
  id: number;
  nameI18n: { [key in Locale]?: string };
  form: MedicationForm;
  dosage: string[] | null; // Changed to string[] | null
  createdByUserId: number | null;
}

// Interface for medication suggestions
export interface MedicationSuggestion {
  id: number | null; // Can be null for user-entered suggestions
  name: string;
  form: string;
  dosage: string[]; // Changed to string[]
  isStructured: boolean;
  isSystem: boolean; // Added isSystem
  usageCount?: number; // Added usageCount
  imageUrl?: string | null; // Added imageUrl
  externalLink?: string | null; // Added externalLink
}

export interface PaginatedMedicationsResponseDto {
  data: MedicationResponseDto[];
  meta: {
    total: number;
    page: number;
    limit: number;
  };
}

export interface CreateMedicationDto {
  nameI18n: { [key in Locale]?: string };
  form: MedicationForm;
  dosage: string[]; // Changed to string[]
  imageId?: number | null; // Added imageId
  externalLink?: string | null; // Added externalLink
}

export interface UpdateMedicationDto {
  nameI18n?: { [key in Locale]?: string };
  form?: MedicationForm;
  dosage?: string[]; // Changed to string[]
  imageId?: number | null; // Added imageId
  externalLink?: string | null; // Added externalLink
}

export interface MergeMedicationDto {
  sourceMedicationId: number;
  targetMedicationId: number;
}

export interface MergeMedicationResponseDto {
  targetMedication: {
    id: number;
    name: string;
    form: string;
  };
  removedMedication: {
    id: number;
    name: string;
    createdByUserId: number;
  };
  updatedCalendarEvents: number;
  message: string;
}

// Query Parameters
export interface GetMedicationsParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: MedicationSortBy;
  sortOrder?: SortOrder;
  form?: MedicationForm;
  // Removed dosage from params
  sourceType?: MedicationSourceType;
  expertId?: number; // Changed from userId to expertId
  isStructured?: boolean; // Added isStructured filter
}

// Medication Intake Payload
export interface MedicationIntakePayload {
  medicationId?: number;
  customName?: string;
  qty?: number;
  dosage?: string; // Added dosage
  scheduledLdt: string; // local datetime string (no Z suffix)
  isCompleted?: boolean;
  calendarId?: number;
}

class MedicationService {
  private readonly BASE_URL = '/lib/medications';
  private readonly INTAKE_URL = '/calendars/events/medication'; // Corrected endpoint
  private readonly CALENDAR_EVENT_URL = '/calendars/events'; // Base URL for calendar events

  async getMedications(params: GetMedicationsParams): Promise<PaginatedMedicationsResponseDto> {
    // We manually handle the response here instead of using unwrapApiResponse
    // because the structure for this endpoint is { success, data: [], meta: {} }
    const response = await api.get<any>(`${this.BASE_URL}`, {
      params,
    });

    const body = response.data;
    if (body && body.success && Array.isArray(body.data)) {
      return {
        data: body.data,
        meta: body.meta,
      };
    }
    
    console.error('Unexpected API response structure for getMedications:', body);
    // Fallback to empty response to avoid crashes downstream
    return { 
      data: [], 
      meta: { total: 0, page: 1, limit: params.limit || 10 } 
    };
  }

  async getMedicationById(id: number): Promise<MedicationResponseDto> {
    const response = await api.get<ApiResponse<MedicationResponseDto>>(`${this.BASE_URL}/${id}`);
    return unwrapApiResponse<MedicationResponseDto>(response);
  }

  async getMedicationForEdit(id: number): Promise<MedicationEditDto> {
    const response = await api.get<ApiResponse<MedicationEditDto>>(`${this.BASE_URL}/${id}/edit`);
    return unwrapApiResponse<MedicationEditDto>(response);
  }

  async createMedication(data: CreateMedicationDto): Promise<MedicationResponseDto> {
    const response = await api.post<ApiResponse<MedicationResponseDto>>(`${this.BASE_URL}`, data);
    return unwrapApiResponse<MedicationResponseDto>(response);
  }

  async updateMedication(id: number, data: UpdateMedicationDto): Promise<MedicationResponseDto> {
    const response = await api.patch<ApiResponse<MedicationResponseDto>>(`${this.BASE_URL}/${id}`, data);
    return unwrapApiResponse<MedicationResponseDto>(response);
  }

  async deleteMedication(id: number): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`${this.BASE_URL}/${id}`);
    return response.data;
  }

  async mergeMedications(data: MergeMedicationDto): Promise<MergeMedicationResponseDto> {
    const response = await api.post<ApiResponse<MergeMedicationResponseDto>>(`${this.BASE_URL}/merge`, data);
    return unwrapApiResponse<MergeMedicationResponseDto>(response);
  }

  async getMedicationSuggestions(query: string, limit: number = 10): Promise<MedicationSuggestion[]> {
    const params: GetMedicationsParams = {
      search: query,
      limit: limit,
    };
    const response = await this.getMedications(params);
    console.log('Response from getMedications in getMedicationSuggestions:', JSON.stringify(response, null, 2));

    if (!response || !Array.isArray(response.data)) {
      console.error('Invalid response structure from getMedications. "response.data" is not an array. Response:', response);
      return [];
    }
    
    return response.data.map(med => ({
      id: med.id,
      name: med.name,
      form: med.form,
      dosage: med.dosage || [], // Ensure it's an array
      isStructured: med.isStructured,
      isSystem: med.isSystem,
    }));
  }

  async createMedicationIntake(payload: MedicationIntakePayload, clientId?: string | number): Promise<any> {
    const url = clientId ? `/client/${clientId}/calendar/events/medication` : this.INTAKE_URL;
    const response = await api.post<ApiResponse<any>>(url, payload);
    return unwrapApiResponse<any>(response);
  }

  async updateMedicationIntake(eventId: number, payload: MedicationIntakePayload): Promise<any> {
    const response = await api.put<ApiResponse<any>>(`${this.CALENDAR_EVENT_URL}/${eventId}/medication`, payload);
    return unwrapApiResponse<any>(response);
  }
}

export const medicationService = new MedicationService();

// Export the function directly for easier import in components that only need suggestions or intake
export const getMedicationSuggestions = (query: string, limit?: number) =>
  medicationService.getMedicationSuggestions(query, limit);

export const createMedicationIntake = (payload: MedicationIntakePayload, clientId?: string | number) =>
  medicationService.createMedicationIntake(payload, clientId);

export const updateMedicationIntake = (eventId: number, payload: MedicationIntakePayload) =>
  medicationService.updateMedicationIntake(eventId, payload);

/** Зберегти препарат з AddMedicationEventModal (модалка вже віддає scheduledLdt). */
export const saveMedicationIntake = (data: any, { eventId, clientId }: SaveIntakeOptions = {}) => {
  const payload: MedicationIntakePayload = {
    scheduledLdt: data.scheduledLdt,
    isCompleted: data.isCompleted,
    calendarId: data.calendarId,
    ...(data.medicationId
      ? {
          medicationId: data.medicationId,
          ...(data.qty && { qty: data.qty }),
          ...(data.dosage && { dosage: data.dosage }),
        }
      : { customName: data.customName }),
  };
  return eventId
    ? medicationService.updateMedicationIntake(eventId, payload)
    : medicationService.createMedicationIntake(payload, clientId);
};
